import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Alert, AuthCard, Button, FieldError, FieldRow, Input, Label } from '@sentinel/ui';
import { requestSchema, type RequestInput } from '../features/reset-password/schema';
import { useRequestReset } from '../features/reset-password/use-request';

type ViewState = 'idle' | 'sent' | 'rate_limited';

function getStatus(e: unknown): number | undefined {
  return (e as { status?: number } | null)?.status;
}

function ResetPasswordRequestPage() {
  const { t } = useTranslation('reset-password');
  const submit = useRequestReset();
  const [view, setView] = useState<ViewState>('idle');
  const [topError, setTopError] = useState<string | null>(null);

  const form = useForm<RequestInput>({ resolver: zodResolver(requestSchema) });

  async function onSubmit(values: RequestInput) {
    setTopError(null);
    try {
      await submit.mutateAsync({ email: values.email });
      // Anti-enumeration: always show generic success on 2xx, regardless of
      // whether the email actually exists in the system.
      setView('sent');
    } catch (e) {
      const status = getStatus(e);
      if (status === 429) {
        setView('rate_limited');
        return;
      }
      setTopError(t('generic_error'));
    }
  }

  if (view === 'sent') {
    return (
      <AuthCard title={t('request_success_title')}>
        <p role="status" aria-live="polite">{t('request_success_message')}</p>
        <p style={{ textAlign: 'center' }}>
          <Link to="/login">{t('back_to_login')}</Link>
        </p>
      </AuthCard>
    );
  }

  if (view === 'rate_limited') {
    return (
      <AuthCard title={t('request_success_title')}>
        <Alert type="error" role="alert">{t('rate_limited')}</Alert>
        <p style={{ textAlign: 'center' }}>
          <Link to="/login">{t('back_to_login')}</Link>
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t('request_title')} subtitle={t('request_subtitle')}>
      {topError ? (
        <Alert type="error" role="alert">{topError}</Alert>
      ) : null}

      <form onSubmit={form.handleSubmit(onSubmit)} style={{ display: 'grid', gap: '0.75rem' }}>
        <FieldRow>
          <Label htmlFor="email">{t('email_label')}</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            aria-invalid={form.formState.errors.email ? 'true' : undefined}
            {...form.register('email')}
          />
          <FieldError message={form.formState.errors.email?.message} />
        </FieldRow>
        <Button type="submit" disabled={submit.isPending}>
          {t('request_submit')}
        </Button>
      </form>

      <p style={{ textAlign: 'center' }}>
        <Link to="/login">{t('back_to_login')}</Link>
      </p>
    </AuthCard>
  );
}

export const Route = createFileRoute('/reset-password/')({
  component: ResetPasswordRequestPage,
});
